import { EditOutlined } from "@ant-design/icons"
import {
  Button,
  Checkbox,
  type CheckboxChangeEvent,
  Empty,
  Flex,
  Progress,
  Space,
  Spin,
  Typography
} from "antd"
import { EntityTable } from "../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../components/EntityTable/surfaces.ts"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../../../constants/placeholders"
import { leadChecklistProgress } from "../../../LeadWorkspacePage/additional/lead-checklist-progress.ts"
import type { LeadChecklistItem } from "../../../types/lead.types.ts"

const { Title, Text } = Typography

interface LeadViewChecklistViewProps {
  checklist: LeadChecklistItem[]
  editAllowed?: boolean
  onEdit: () => void
  onUpdate: (checklist: LeadChecklistItem[]) => Promise<void>
}

export function LeadViewChecklistView({
  checklist,
  editAllowed,
  onEdit,
  onUpdate
}: LeadViewChecklistViewProps) {
  const { t } = useTranslation()
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null)

  const { total, completed, percent } = leadChecklistProgress(checklist)

  const onCheckedChanged = async (e: CheckboxChangeEvent, index: number) => {
    try {
      const newChecklist = checklist.map((item, i) =>
        i === index ? { ...item, is_done: e?.target?.checked } : item
      )

      setLoadingIndex(index)
      await onUpdate(newChecklist)
    } finally {
      setLoadingIndex(null)
    }
  }

  return (
    <Space direction={"vertical"} className={"w-full"}>
      <Flex align={"center"} justify={"space-between"}>
        <Title level={5}>{t("leadWorkspace.cards.checklist.title")}</Title>
        {editAllowed && (
          <Button
            loading={loadingIndex !== null}
            onClick={onEdit}
            aria-label={t("leadWorkspace.cards.checklist.edit")}
            icon={<EditOutlined />}
          />
        )}
      </Flex>
      <Flex gap={"small"}>
        <Progress
          showInfo={false}
          percent={percent}
          status={percent === 100 ? "success" : "active"}
        />
        <Text className={"flex-shrink-0"} type={"secondary"}>
          {t("leadWorkspace.cards.checklist.progress", { completed, total })}
        </Text>
      </Flex>

      {checklist?.length ? (
        <EntityTable
          surface={TABLE_SURFACES.leadChecklistView}
          tab={DEFAULT_TAB}
          hideConfigButton
          size={"small"}
          withOutPagination
          pagination={false}
          dataSource={checklist}
          columns={[
            {
              title: "",
              dataIndex: "is_done",
              key: "is_done",
              width: 35,
              hidden: !editAllowed,
              render: (done: boolean, item, index) => {
                const loading = loadingIndex === index

                return (
                  <Spin spinning={loading} size="small">
                    <Checkbox
                      onChange={(e) => onCheckedChanged(e, index)}
                      checked={done}
                      disabled={loading}
                      aria-label={item?.title}
                    />
                  </Spin>
                )
              }
            },
            {
              title: String(t("leadWorkspace.cards.checklist.task")),
              dataIndex: "title",
              key: "title",
              render: (text: string) => <Text>{text || empty}</Text>
            }
          ]}
        />
      ) : (
        <Empty
          description={t("leadWorkspace.cards.checklist.empty")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className={"m-0"}
        />
      )}
    </Space>
  )
}
